import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, Copy, Check, Loader2 } from 'lucide-react';
import { getData, setData } from '../services/storage';
import { Modal } from '../components/Forms';

const MediaManager = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [copied, setCopied] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setFiles(getData('media') || []);
    setLoading(false);
  }, []);

  const saveFiles = (updated) => {
    setFiles(updated);
    setData('media', updated);
  };

  const handleUpload = (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles?.length) return;
    setUploading(true);
    const newFiles = Array.from(selectedFiles).map((f) => ({
      _id: Date.now().toString() + Math.random().toString(36).slice(2, 6),
      originalName: f.name,
      name: f.name,
      size: f.size,
      mimeType: f.type,
      url: URL.createObjectURL(f),
      folder: 'general',
      createdAt: new Date().toISOString(),
    }));
    const updated = [...files, ...newFiles];
    saveFiles(updated);
    setUploading(false);
    fileRef.current.value = '';
  };

  const handleDelete = (file) => {
    if (!window.confirm(`Delete "${file.originalName}"?`)) return;
    saveFiles(files.filter((f) => f._id !== file._id));
    setSelectedFile(null);
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isImage = (mime) => mime?.startsWith('image/');

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <input ref={fileRef} type="file" multiple accept="image/*" onChange={handleUpload} className="hidden" id="file-upload" />
          <label htmlFor="file-upload" className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg text-sm font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg shadow-purple-500/25 cursor-pointer">
            {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
            {uploading ? 'Uploading...' : 'Upload Images'}
          </label>
        </div>
        <span className="text-sm text-slate-500">{files.length} files</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-purple-500" size={32} /></div>
      ) : files.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <Upload className="mx-auto text-slate-300 mb-4" size={48} />
          <p className="text-slate-500">No media files yet. Upload your first image!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {files.map((file) => (
            <div key={file._id} className="group relative bg-white rounded-xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all">
              <div className="aspect-square overflow-hidden bg-slate-100 cursor-pointer" onClick={() => setSelectedFile(file)}>
                {isImage(file.mimeType) ? (
                  <img src={file.url} alt={file.originalName} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-400 text-sm">{file.originalName}</div>
                )}
              </div>
              <div className="p-2">
                <p className="text-xs text-slate-600 truncate">{file.originalName}</p>
                <p className="text-xs text-slate-400">{(file.size / 1024).toFixed(1)} KB</p>
              </div>
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => copyUrl(file.url)} className="p-1.5 bg-white/90 backdrop-blur-sm rounded-lg shadow hover:bg-white text-slate-600 hover:text-purple-600 transition-colors">
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </button>
                <button onClick={() => handleDelete(file)} className="p-1.5 bg-white/90 backdrop-blur-sm rounded-lg shadow hover:bg-white text-slate-600 hover:text-red-600 transition-colors">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={!!selectedFile} onClose={() => setSelectedFile(null)} title="Preview" size="lg">
        {selectedFile && (
          <div className="space-y-4">
            {isImage(selectedFile.mimeType) ? (
              <img src={selectedFile.url} alt={selectedFile.originalName} className="w-full max-h-[60vh] object-contain rounded-lg" />
            ) : (
              <p className="text-center text-slate-500 py-12">Preview not available for this file type</p>
            )}
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div><span className="font-medium text-slate-700">Name:</span> <span className="text-slate-500">{selectedFile.originalName}</span></div>
              <div><span className="font-medium text-slate-700">Size:</span> <span className="text-slate-500">{(selectedFile.size / 1024).toFixed(1)} KB</span></div>
              <div><span className="font-medium text-slate-700">Type:</span> <span className="text-slate-500">{selectedFile.mimeType}</span></div>
              <div><span className="font-medium text-slate-700">URL:</span> <span className="text-slate-500 truncate block">{selectedFile.url}</span></div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <button onClick={() => copyUrl(selectedFile.url)} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition-colors">
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? 'Copied!' : 'Copy URL'}
              </button>
              <button onClick={() => handleDelete(selectedFile)} className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors">
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MediaManager;
